"use client"

import React, { useState, useEffect } from "react"
import { Calendar, ChevronDown } from "lucide-react"
import dayjs from "dayjs"

type FilterPreset = "today" | "last7" | "last30" | "last6months" | "lastyear" | "custom"

interface DateRange {
  startDate: string
  endDate: string
}

interface DateFilterProps {
  defaultPreset?: FilterPreset
  onChange: (range: DateRange) => void
}

const PRESETS: { label: string; value: FilterPreset }[] = [
  { label: "Today", value: "today" },
  { label: "Last 7 Days", value: "last7" },
  { label: "Last 30 Days", value: "last30" },
  { label: "Last 6 Months", value: "last6months" },
  { label: "Last Year", value: "lastyear" },
  { label: "Custom", value: "custom" },
]

const computeRange = (preset: FilterPreset): DateRange => {
  const today = dayjs().format("YYYY-MM-DD")
  const ranges: Record<FilterPreset, DateRange> = {
    today: { startDate: today, endDate: today },
    last7: { startDate: dayjs().subtract(6, "day").format("YYYY-MM-DD"), endDate: today },
    last30: { startDate: dayjs().subtract(29, "day").format("YYYY-MM-DD"), endDate: today },
    last6months: { startDate: dayjs().subtract(6, "month").format("YYYY-MM-DD"), endDate: today },
    lastyear: { startDate: dayjs().subtract(1, "year").format("YYYY-MM-DD"), endDate: today },
    custom: { startDate: today, endDate: today },
  }
  return ranges[preset]
}

const DateFilter: React.FC<DateFilterProps> = ({ defaultPreset = "last7", onChange }) => {
  const [activePreset, setActivePreset] = useState<FilterPreset>(defaultPreset)
  const [customRange, setCustomRange] = useState<DateRange>(computeRange("custom"))
  const [showCustom, setShowCustom] = useState(false)
  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    if (defaultPreset !== "custom") {
      onChange(computeRange(defaultPreset))
    }
  }, [])

  const handlePresetSelect = (preset: FilterPreset) => {
    setActivePreset(preset)
    if (preset === "custom") {
      setShowCustom(true)
      setIsOpen(false)
      return
    }
    setShowCustom(false)
    setIsOpen(false)
    onChange(computeRange(preset))
  }

  const handleCustomApply = () => {
    setShowCustom(false)
    onChange(customRange)
  }

  const activeLabel = PRESETS.find((p) => p.value === activePreset)?.label ?? "Filter"

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <div className="relative">
        <button
          onClick={() => setIsOpen((prev) => !prev)}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
        >
          <Calendar size={14} />
          {activeLabel}
          <ChevronDown size={14} />
        </button>

        {isOpen && (
          <div className="absolute left-0 top-full z-50 mt-1 w-44 rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
            {PRESETS.map((preset) => (
              <button
                key={preset.value}
                onClick={() => handlePresetSelect(preset.value)}
                className={`w-full px-4 py-2 text-left text-sm transition-colors hover:bg-gray-50 dark:hover:bg-gray-700 ${
                  activePreset === preset.value
                    ? "bg-purple-50 text-purple-700 font-medium dark:bg-purple-900/20 dark:text-purple-300"
                    : "text-gray-700 dark:text-gray-300"
                }`}
              >
                {preset.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {showCustom && (
        <div className="flex items-center gap-2 flex-wrap">
          <input
            type="date"
            value={customRange.startDate}
            onChange={(e) => setCustomRange((prev) => ({ ...prev, startDate: e.target.value }))}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
          />
          <span className="text-sm text-gray-500">to</span>
          <input
            type="date"
            value={customRange.endDate}
            onChange={(e) => setCustomRange((prev) => ({ ...prev, endDate: e.target.value }))}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
          />
          <button
            onClick={handleCustomApply}
            className="rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700"
          >
            Apply
          </button>
        </div>
      )}
    </div>
  )
}

export default DateFilter